// components/document-settings/PlaceholdersTab.jsx
// Gestione placeholder documenti

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Code, Copy, Search } from 'lucide-react';
import { toast } from 'sonner';

const categoryLabels = {
  'customer': 'Cliente',
  'property': 'Immobile',
  'estimate': 'Preventivo',
  'contract': 'Contratto',
  'company': 'Azienda',
  'dates': 'Date',
  'amounts': 'Importi',
  'items': 'Voci',
  'sections': 'Sezioni'
};

export const PlaceholdersTab = ({
  placeholders,
  placeholderFilter,
  setPlaceholderFilter
}) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(`{{${text}}}`);
    toast.success(`Copiato: {{${text}}}`);
  };

  const filteredPlaceholders = placeholders.filter(p => 
    placeholderFilter === 'all' || p.category === placeholderFilter
  );

  const categories = [...new Set(placeholders.map(p => p.category))];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Placeholder Disponibili</h3>
          <p className="text-sm text-gray-500">Variabili da usare nei template DOCX</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={placeholderFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPlaceholderFilter('all')}
            className={placeholderFilter === 'all' ? 'bg-[#FF7819] hover:bg-[#E56A10]' : ''}
          >
            Tutti
          </Button>
          {categories.map(cat => (
            <Button
              key={cat}
              variant={placeholderFilter === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPlaceholderFilter(cat)}
              className={placeholderFilter === cat ? 'bg-[#FF7819] hover:bg-[#E56A10]' : ''}
            >
              {categoryLabels[cat] || cat}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Code className="w-5 h-5" />
            {filteredPlaceholders.length} Placeholder
            {placeholderFilter !== 'all' && ` in "${categoryLabels[placeholderFilter] || placeholderFilter}"`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredPlaceholders.map(placeholder => (
              <div 
                key={placeholder.key}
                className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer group"
                onClick={() => copyToClipboard(placeholder.key)}
              >
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                    {`{{${placeholder.key}}}`}
                  </code>
                  <Copy className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                </div>
                <p className="text-sm text-gray-600 mt-1">{placeholder.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    {categoryLabels[placeholder.category] || placeholder.category}
                  </Badge>
                  {placeholder.example && (
                    <span className="text-xs text-gray-400">es: {placeholder.example}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Come usare i placeholder</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-gray-600">
          <p>
            <strong>Sintassi base:</strong> Usa <code className="bg-gray-100 px-1 rounded">{`{{nome_placeholder}}`}</code> nel tuo documento DOCX.
          </p>
          <p>
            <strong>Tabelle:</strong> Per le righe ripetute (come voci preventivo), usa:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><code className="bg-gray-100 px-1 rounded">{`{%tr for item in items%}`}</code> - inizio ciclo</li>
            <li><code className="bg-gray-100 px-1 rounded">{`{{item.description}}`}</code> - campo della voce</li>
            <li><code className="bg-gray-100 px-1 rounded">{`{%tr endfor%}`}</code> - fine ciclo</li>
          </ul>
          <p>
            <strong>Condizioni:</strong> Usa <code className="bg-gray-100 px-1 rounded">{`{%if condizione%}...{%endif%}`}</code> per contenuti condizionali.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlaceholdersTab;
